import React, { useEffect, useState } from 'react';

import { format } from 'date-fns';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { IServerOrder } from '@rbi-ctg/menu';
import OrderCancelledModal from 'components/order-cancelled-modal';
import useDialogModal from 'hooks/use-dialog-modal';
import useErrorModal from 'hooks/use-error-modal';
import { useEtaCounter } from 'hooks/use-eta-counter/';
import usePolling from 'hooks/use-polling';
import fetchDeliveryStatus from 'remote/api/delivery-status';
import { useDeliveryConfirmationContext } from 'state/delivery-confirmation';
import { DeliveryStatus } from 'state/order/types';
import { useUIContext } from 'state/ui';
import { computeDeliveryFee } from 'utils/cart/helper';
import { routes } from 'utils/routing';

import CourierDetail from './courier-detail';
import DeliveryProgress from './delivery-progress';
import { messages } from './messages';
import {
  Card,
  CardHeading,
  CardText,
  DeliveryCardContainer,
  ETA,
  FeeDeliveryItem,
  TextGroup,
  TotalRow
} from './styled';

interface IDeliveryDetailsProps {
  serverOrder: IServerOrder;
}

const DeliveryDetails: React.FC<IDeliveryDetailsProps> = ({ serverOrder }) => {
  const { delivery, rbiOrderId } = serverOrder;
  const [deliveryStatus, setDeliveryStatus] = useState(delivery!.status);
  const [driver, setDriver] = useState(delivery!.driver || null);
  const { formatMessage } = useIntl();
  const { formatCurrencyForLocale } = useUIContext();
  const eta = useEtaCounter(delivery!);
  const { setBringgUuids } = useDeliveryConfirmationContext();
  const navigate = useNavigate();

  const { stopPolling } = usePolling({
    queryOptions: fetchDeliveryStatus(rbiOrderId),
    interval: 5000,
    retry: 3,
    onResponse: data => {
      setDeliveryStatus(data?.delivery?.status);
      setDriver(data?.delivery?.driver);
      // Needed to track bringg delivery driver coords
      const share_uuid = data?.delivery?.shareUuid;
      const order_uuid = data?.delivery?.task?.uuid;
      setBringgUuids({ share_uuid, order_uuid });
    },
    onError: () => {
      openErrDialog({
        message: 'There was an error fetching your delivery status. Please try again later.'
      });
    }
  });

  const [ErrorDialog, openErrDialog] = useErrorModal({
    onConfirm: () => navigate(routes.orders),
    modalAppearanceEventMessage: 'Error: Fetching Delivery Details Failure'
  });

  const [CancellationModal, openCancellationModal] = useDialogModal({
    Component: OrderCancelledModal,
    onOpen: stopPolling,
    onDismiss: () => navigate(routes.base)
  });

  useEffect(() => {
    if (deliveryStatus === DeliveryStatus.ORDER_CANCELLED) {
      openCancellationModal();
    }
  }, [deliveryStatus, openCancellationModal]);

  if (!delivery) {
    return null;
  }

  const cardType = serverOrder?.cart?.payment?.cardType || null;
  const ccLast4 = serverOrder?.cart?.payment?.ccLast4 || null;

  const feeCents = delivery?.feeCents || 0;
  const feeDiscountCents = delivery?.feeDiscountCents || 0;
  const serviceFeeCents = delivery.serviceFeeCents || 0;
  const smallCartFeeCents = delivery?.smallCartFeeCents || 0;

  const deliveryFeeCents = computeDeliveryFee({
    feeCents,
    feeDiscountCents,
    serviceFeeCents,
    smallCartFeeCents
  });
  const isFreeDelivery = deliveryFeeCents === 0;
  const DeliveryFee = () =>
    isFreeDelivery ? (
      <FeeDeliveryItem>
        {formatMessage(messages.free)}
        &nbsp;<s>{formatCurrencyForLocale(feeDiscountCents)}</s>
      </FeeDeliveryItem>
    ) : (
      <CardText>{formatCurrencyForLocale(deliveryFeeCents)}</CardText>
    );

  // TODO: check for cancelled orders and orders that have already been delivered
  return (
    <>
      <DeliveryCardContainer>
        <Card>
          <ETA>
            {formatMessage(messages.eta)}: <strong>{eta || '...'}</strong>
          </ETA>

          <CardHeading data-testid="order-preparation-confirmation" data-private>
            {formatMessage(messages.deliveringToYourDoorAt)}
            <br />
            {delivery.dropoff.addressLine1}
          </CardHeading>
          <DeliveryProgress deliveryStatus={deliveryStatus} rbiOrderId={serverOrder.rbiOrderId} />
          {driver && <CourierDetail driver={driver} />}
        </Card>

        <Card>
          <CardHeading>{formatMessage(messages.orderDetailsHeading)}</CardHeading>
          <TextGroup>
            <CardText>
              {formatMessage(messages.orderNumber)}: {serverOrder.cart.ticketNumber}
            </CardText>
            {cardType && ccLast4 && (
              <CardText>
                {formatMessage(messages.paymentXEndingInY, {
                  cardType,
                  ccLast4
                })}
              </CardText>
            )}
            <CardText>
              {formatMessage(messages.orderTime)}:{' '}
              {format(new Date(serverOrder.createdAt), 'h:mm aaaa')}
            </CardText>
          </TextGroup>

          <CardHeading>{formatMessage(messages.deliveryInfo)}</CardHeading>
          <TextGroup>
            <CardText data-private>
              <strong>{serverOrder.cart.customerName}</strong>
            </CardText>
            <CardText data-private>
              {delivery.dropoff.addressLine1}
              <br />
              {delivery.dropoff.addressLine2 && (
                <>
                  {formatMessage(
                    { id: 'unitNumber' },
                    { unitNumber: delivery.dropoff.addressLine2 }
                  )}
                  <br />
                </>
              )}
              {delivery.dropoff.city}, {delivery.dropoff.state} {delivery.dropoff.zip}
            </CardText>
            {delivery.dropoff.phoneNumber && (
              <CardText data-private>{delivery.dropoff.phoneNumber}</CardText>
            )}
            {delivery.instructions && (
              <>
                <br />
                <CardText data-private>
                  {formatMessage(messages.instructions)}: {delivery.instructions}
                </CardText>
              </>
            )}
          </TextGroup>

          <CardHeading>{formatMessage(messages.yourOrder)}</CardHeading>
          <TextGroup>
            {serverOrder.cart.cartEntries.map((cartEntry: any) => (
              <div key={cartEntry.name}>
                <CardText>
                  <strong>
                    {cartEntry.quantity && cartEntry.quantity > 1
                      ? `${cartEntry.quantity}x ${cartEntry.name} `
                      : `${cartEntry.name} `}
                  </strong>
                </CardText>
                <CardText>{formatCurrencyForLocale(cartEntry.price)}</CardText>
              </div>
            ))}
          </TextGroup>

          <TotalRow>
            <CardText>{formatMessage(messages.subtotal)}</CardText>
            <CardText>{formatCurrencyForLocale(serverOrder.cart.subTotalCents)}</CardText>
          </TotalRow>
          <TotalRow>
            <CardText>{formatMessage(messages.tax)}</CardText>
            <CardText>{formatCurrencyForLocale(serverOrder.cart.taxCents)}</CardText>
          </TotalRow>
          <TotalRow>
            <CardText>{formatMessage(messages.deliveryFee)}</CardText>
            <DeliveryFee />
          </TotalRow>
          {serviceFeeCents > 0 && (
            <TotalRow>
              <CardText>{formatMessage(messages.serviceFee)}</CardText>
              <CardText>{formatCurrencyForLocale(serviceFeeCents)}</CardText>
            </TotalRow>
          )}
          {smallCartFeeCents > 0 && (
            <TotalRow>
              <CardText>{formatMessage(messages.smallCartFee)}</CardText>
              <CardText>{formatCurrencyForLocale(smallCartFeeCents)}</CardText>
            </TotalRow>
          )}
          <TotalRow>
            <CardText>{formatMessage(messages.tip)}</CardText>
            <CardText>{formatCurrencyForLocale(delivery.tipCents)}</CardText>
          </TotalRow>
          <br />
          <TotalRow>
            <CardText>
              <strong>{formatMessage(messages.total)}</strong>
            </CardText>
            <CardText>
              <strong>{formatCurrencyForLocale(serverOrder.cart.totalCents)}</strong>
            </CardText>
          </TotalRow>
        </Card>
      </DeliveryCardContainer>
      <ErrorDialog />
      <CancellationModal />
    </>
  );
};

export default DeliveryDetails;
